import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Users } from "lucide-react";
import {
  VentureCard,
  VentureCardContent,
  VentureCardHeader,
  VentureCardTitle,
} from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { supabase } from "@/integrations/supabase/client";

interface FunnelRow {
  id: string;
  contact_name: string | null;
  stage: string;
  platform: string | null;
  lead_created_at: string | null;
  demo_booked_at: string | null;
  demo_showed_at: string | null;
  created_at: string | null;
}

export default function Leads() {
  const { data: vhMember, isLoading: memberLoading } = useQuery({
    queryKey: ["my-vh-team-member-row"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from("vh_team_members")
        .select("id, name")
        .eq("user_id", user.id)
        .eq("role", "setter")
        .maybeSingle();
      return data;
    },
    staleTime: 60_000,
  });

  const { data: leads, isLoading: leadsLoading } = useQuery({
    queryKey: ["setter-leads", vhMember?.id],
    enabled: !!vhMember?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("funnel_events")
        .select(
          "id, contact_name, stage, platform, lead_created_at, demo_booked_at, demo_showed_at, created_at"
        )
        .eq("setter_id", vhMember!.id)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as FunnelRow[];
    },
    staleTime: 60_000,
  });

  if (memberLoading) {
    return <div className="p-4">Loading…</div>;
  }

  if (!vhMember) {
    return (
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>My Leads</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <p className="text-muted-foreground">
            No setter profile linked — ask admin to link your account.
          </p>
        </VentureCardContent>
      </VentureCard>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Users className="w-6 h-6 text-secondary" />
        <h1 className="text-2xl font-bold">My Leads</h1>
      </div>

      <VentureCard>
        <VentureCardContent>
          {leadsLoading ? (
            <div className="py-6 text-muted-foreground">Loading leads…</div>
          ) : (leads ?? []).length === 0 ? (
            <div className="py-6 text-muted-foreground text-center">
              No leads yet.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {(leads ?? []).map((l) => (
                <div
                  key={l.id}
                  className="py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {l.contact_name || "(unnamed lead)"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {l.platform ?? "—"}
                      {l.lead_created_at && (
                        <>
                          {" · "}
                          {format(new Date(l.lead_created_at), "MMM d, yyyy")}
                        </>
                      )}
                    </p>
                  </div>
                  <BadgeVenture
                    variant={
                      l.demo_showed_at
                        ? "success"
                        : l.demo_booked_at
                        ? "info"
                        : "default"
                    }
                  >
                    {l.stage}
                  </BadgeVenture>
                </div>
              ))}
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
